import { useState, ReactNode } from 'react';
import { Box, Collapse, IconButton, Stack } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';

import { ArrowDownIcon, ArrowUpIcon } from '@ocx/ui-icons';
import { SizingMultiplier, Typography } from '@ocx/ui';
import { useLoyaltyCurrencyFormat } from '@ocx/feat-loyalty-currency';

import { ReceiptOffer } from '../../../../receipts.types';
import { ReceiptPromotionChip } from '../../../receipt-body/components/receipt-promotion-chip.components';
import { TransactionIconBox } from '../../../transaction-icon-box';
import { TwoColsTypography } from '../../../two-cols-typography';
import { messages } from '../../receipt-loyalty-card.messages';

export type LoyaltyItemContentProps = {
  icon: ReactNode;
  iconBackgroundColor?: string;
  title: ReactNode;
  value: ReactNode;
  appliedOffersNumber: number;
  offers?: ReceiptOffer[];
};

export const LoyaltyItemContent = (props: LoyaltyItemContentProps) => {
  const { icon, iconBackgroundColor, title, value, appliedOffersNumber, offers = [] } = props;
  const intl = useIntl();
  const [isExpanded, setIsExpanded] = useState(false);
  const hasOffers = offers.length > 0;
  const formatLoyaltyCurrency = useLoyaltyCurrencyFormat();

  return (
    <Box>
      <Stack direction="row" spacing={SizingMultiplier.sm} alignItems="center">
        <TransactionIconBox {...(iconBackgroundColor && { backgroundColor: iconBackgroundColor })}>
          {icon}
        </TransactionIconBox>
        <Box flex={1}>
          <TwoColsTypography title={title} value={value} variant="subtitle1" />
          {appliedOffersNumber > 0 && (
            <Typography variant="body2" color="text.disabled" mt={SizingMultiplier.xs}>
              <FormattedMessage {...messages['receipt:loyalty-card:offers-applied']} values={{ appliedOffersNumber }} />
            </Typography>
          )}
        </Box>
        {hasOffers && (
          <IconButton
            size="small"
            aria-label="toggle offers list"
            aria-expanded={isExpanded}
            edge="end"
            onClick={() => setIsExpanded((prev) => !prev)}>
            {isExpanded ? <ArrowUpIcon fontSize="small" /> : <ArrowDownIcon fontSize="small" />}
          </IconButton>
        )}
      </Stack>

      {hasOffers && (
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Stack spacing={SizingMultiplier.sm} mt={SizingMultiplier.sm}>
            {offers.map((offer) => (
              <Stack key={offer.id} direction="row" spacing={SizingMultiplier.sm} alignItems="center">
                <Box flex={1}>
                  <ReceiptPromotionChip
                    label={offer.name || intl.formatMessage(messages['receipt:loyalty-card:offer-name-fallback'])}
                    fullWidth
                  />
                </Box>
                <Typography variant="body2" color="text.disabled">
                  {formatLoyaltyCurrency(offer.quantity, { signDisplay: 'exceptZero' })}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Collapse>
      )}
    </Box>
  );
};
